import {nameof} from "ts-simple-nameof";
import {Localizer} from '../../../infrastructure/localization/localizer';
import {useCallback, useEffect, useRef, useState} from "react";
import {CrudForloebInputModel} from "./crudForloebInputModel";
import {FormikProps} from "formik";
import {ValidationSwitch} from "../../components/validation/components/ValidationSwitch";
import {ValidationDatepicker} from "../../components/validation/components/validationDatePicker";
import {DatetimePickerModule} from "ditmer-embla";
import {DatePickerActionButton} from "core/components/datePicker/datePickerActionButton";
import {calculateEndDate} from "./forloebHelpers";
import {EmblaIcons} from "../../emblaIcons";
import {ValidationDropdown} from "core/components/validation/components/validationDropdown";
import {toDropdownOptions} from "core/sharedmodels/forSelectModel";
import {DropdownOption} from "core/components/dropdown/dropdown";
import {LedigStillingModel} from "core/sharedmodels/stilling/ledigStillingModel";
import useEnumDropdownOptions from "core/components/dropdown/hooks/useEnumDropdownOptions";
import {FravaerskodeEnum, GetFravaerskodeTranslation} from "core/sharedmodels/forloeb/fravaerskoderEnum";
import useLogbogApi from "core/hooks/useLogbogApi";
import useUser from "../../hooks/useUser";
import classNames from "classnames";
import {ValidationInput} from "../../components/validation/components/validationInput";
import useForloebDuration from "./hooks/useForloebDuration";

type forloebCrudProps = {
    modalId: string
    formik: FormikProps<CrudForloebInputModel>
    stillingReadonly?: boolean
    forloebIsAfsluttet?: boolean
    forloebIsGodkendt?: boolean
    forloebGodkendtDato?: string
    uddannelseslaegeId?: string
    forloebFraVU?: boolean,
    forloebIsEvalueret?: boolean
    isCreateFromKladde?: boolean,
    isKladde?: boolean,
}

const useSelectedStilling = (formik: FormikProps<CrudForloebInputModel>, selectedStartDate?: Date, selectedEndDate?: Date) => {
    const [selectedStilling, setSelectedStilling] = useState<LedigStillingModel>();
    const [initialSelectedStillingOption, setIntialSelectedStillingOption] = useState<DropdownOption<string>>();
    const { logbogStillingApi } = useLogbogApi();

    useEffect(() => {
        const fetchInitialStilling = async () => {
            const initialStillingId = formik.initialValues.stillingId;
            const initialStartDato = formik.initialValues.startDato;
            const initialSlutDato = formik.initialValues.slutDato;

            if (!!initialStillingId && !!initialStartDato && !!initialSlutDato) {
                const initialSelectedStilling = await logbogStillingApi.getLedigStilling(initialStillingId, initialStartDato, initialSlutDato);
                const initialSelectedStillingOption = {
                    label: initialSelectedStilling.nummer,
                    value: initialSelectedStilling.id
                } as DropdownOption<string>;
                setIntialSelectedStillingOption(initialSelectedStillingOption);
            }
        }
        fetchInitialStilling();
    }, [formik.initialValues.slutDato, formik.initialValues.startDato, formik.initialValues.stillingId, logbogStillingApi]);

    const formikSetFieldValue = formik.setFieldValue;

    const onStillingSelected = async (id?: string) => {
        if (!id) {
            setSelectedStilling(undefined);
            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.erFravaer), formik.initialValues.erFravaer);
            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), formik.initialValues.skalIndgaaIEvalueringsstatistik);
            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.fravaerskode), formik.initialValues.fravaerskode);
            return;
        }

        if (!!selectedStartDate && !!selectedEndDate) {
            const stilling = await logbogStillingApi.getLedigStilling(id, selectedStartDate, selectedEndDate);
            setSelectedStilling(stilling);

            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.erFravaer), stilling.erAltidFravaer);

            if(formik.initialValues.senesteDatoForEvaluering !== undefined && !formik.values.erFravaer){
                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), formik.initialValues.skalIndgaaIEvalueringsstatistik);
            } else if(formik.values.erFravaer){
                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), false);
            }
        }
    };

    return {
        initialSelectedStillingOption,
        selectedStilling,
        onStillingSelected
    }
}

export const maxAmountMonthsBeforeSkalIndgaaIEvalueringsstatistik = 3;
export const monthsLatestEvalDateIsFromEndDate = 6;

export function ForloebCrudFields({isCreateFromKladde, isKladde, ...props}: forloebCrudProps) {
    const [selectedEndDate, setSelectedEndDate] = useState<Date | undefined>(props.formik.initialValues.slutDato);
    const [selectedSenestEvalueringsdato, setSelectedSenestEvalueringsdato] = useState<Date | undefined>(props.formik.initialValues.senesteDatoForEvaluering);
    const [selectedStartDate, setSelectedStartDate] = useState<Date | undefined>(props.formik.initialValues.startDato);
    const [fravaerskoderTypeSelectOptions] = useEnumDropdownOptions<FravaerskodeEnum>(FravaerskodeEnum, GetFravaerskodeTranslation, [FravaerskodeEnum.FravaerMidlertidig]);

    const { logbogStillingApi, logbogUserInfoApi } = useLogbogApi();

    const { initialSelectedStillingOption, selectedStilling, onStillingSelected } = useSelectedStilling(props.formik, selectedStartDate, selectedEndDate);

    const prevEndDate = useRef<Date | undefined>(props.formik.values.slutDato);
    const prevStartDate = useRef<Date | undefined>(props.formik.values.startDato);

    const slutDateTimePickerModuleRef = useRef<DatetimePickerModule>(null);
    const senestEvalueringsdatoDateTimePickerModuleRef = useRef<DatetimePickerModule>(null);

    const forloebGodkendt = props.forloebGodkendtDato !== undefined && props.forloebGodkendtDato !== null;
    const forloebErEvalueret = props.formik.values.evalueretDato !== undefined && props.formik.values.evalueretDato !== null;

    const { currentUser } = useUser();
    const { forloebDuration } = useForloebDuration(selectedStartDate, selectedEndDate);

    // Solves dependency-array:
    const formikSetFieldValue = props.formik.setFieldValue;
    const prevSkalIndgaaIEvalueringsstatistik = useRef<boolean>(props.formik.values.skalIndgaaIEvalueringsstatistik);

    const getLatestEvalDate = useCallback((endDate: Date) => {
        const shouldKeepInitialDate =
            !!props.formik.initialValues.senesteDatoForEvaluering
            && (!prevEndDate.current || !prevSkalIndgaaIEvalueringsstatistik.current);

        return shouldKeepInitialDate ? props.formik.initialValues.senesteDatoForEvaluering : endDate.addMonths(monthsLatestEvalDateIsFromEndDate);
    }, [props.formik.initialValues.senesteDatoForEvaluering]);

    useEffect(() => {
        prevSkalIndgaaIEvalueringsstatistik.current = props.formik.values.skalIndgaaIEvalueringsstatistik;
    }, [props.formik.values.skalIndgaaIEvalueringsstatistik]);
    
    useEffect(() => {
        const handleDateChange = () => {
            if (selectedEndDate === undefined || selectedStartDate === undefined || forloebDuration === undefined) return;

            const hasEndDateChanged = selectedEndDate.dateWithoutTimeFormat(true) !== prevEndDate.current?.dateWithoutTimeFormat(true);
            const hasStartDateChanged = selectedStartDate.dateWithoutTimeFormat(true) !== prevStartDate.current?.dateWithoutTimeFormat(true);

            if (!hasEndDateChanged && !hasStartDateChanged) return;

            const senesteEvalueringsdato = getLatestEvalDate(selectedEndDate);

            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.senesteDatoForEvaluering), senesteEvalueringsdato);
            setSelectedSenestEvalueringsdato(senesteEvalueringsdato);

            if(senesteEvalueringsdato)
                senestEvalueringsdatoDateTimePickerModuleRef.current?.setValue(senesteEvalueringsdato);

            if(props.formik.values.erFravaer){
                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.senesteDatoForEvaluering), null);
                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), false);
                return;
            }

            const isPeriodDurationMoreThanMaxAllowedDays = forloebDuration.months >= maxAmountMonthsBeforeSkalIndgaaIEvalueringsstatistik;
            const skalIndgaaIEvalueringsstatistik = isPeriodDurationMoreThanMaxAllowedDays && !props.formik.values.irrelevant && !props.formik.values.forloebKladde;
            formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), skalIndgaaIEvalueringsstatistik);

            if(isPeriodDurationMoreThanMaxAllowedDays) {
                if(!senesteEvalueringsdato) {
                    senestEvalueringsdatoDateTimePickerModuleRef.current?.setValue(selectedEndDate.addMonths(monthsLatestEvalDateIsFromEndDate));
                }
                return;
            }

            prevEndDate.current = selectedEndDate;
            prevStartDate.current = selectedStartDate;
        }

        handleDateChange();
    }, [formikSetFieldValue, getLatestEvalDate, props.formik.values.erFravaer, props.formik.values.irrelevant, props.formik.values.forloebKladde, selectedEndDate, selectedStartDate, forloebDuration]);

    const fetchAsyncLedigeStillingerOptions = async (search: string) => {
        if(!!selectedStartDate && !!selectedEndDate) {
            const selectModels = await logbogStillingApi.getLedigeStillingerForSelect(selectedStartDate, selectedEndDate, search);
            return toDropdownOptions(selectModels);
        }

        return [];
    }

    const fetchAsyncLaegerOptions = async (search: string) => {
        const selectModels = await logbogUserInfoApi.getLaegerSelectForCreateForloeb(search);
        return toDropdownOptions(selectModels);
    }

    return (
        <>
            <div className="flex-row">
                <div>
                    {!props.formik.initialValues.brugerId &&
                        <ValidationDropdown
                            model={{
                                label: Localizer.laege(),
                                placeholder: Localizer.soegNavnEllerFoedselsdag(),
                                htmlName: nameof<CrudForloebInputModel>(x => x.brugerId)
                            }}
                            fetchAsyncOptions={fetchAsyncLaegerOptions}
                            fetchAsyncMinSearchLength={3}
                            readOnly={props.formik.values.forloebKladde}
                            formikProps={props.formik}
                            noMarginBottom
                            itemSelected={() => {
                                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.forloebKladde), false);
                            }}
                        />
                    }
                    { isKladde &&
                        <ValidationSwitch
                            model={{
                                label: Localizer.forloebpage_planlagtForloeb(),
                                placeholder: "",
                                htmlName: nameof<CrudForloebInputModel>(x => x.forloebKladde)
                            }}
                            labelInfoTooltipText={Localizer.forloebpage_kladdeDescription()}
                            inputId={nameof<CrudForloebInputModel>(x => x.forloebKladde) + props.modalId}
                            formikProps={props.formik}
                            readOnly={!!props.formik.values.brugerId}
                            onChangeCallback={(e) => {
                                const kladde = e.target.checked;
                                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), kladde
                                    ? false
                                    : props.formik.initialValues.skalIndgaaIEvalueringsstatistik);
                                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.irrelevant), kladde
                                    ? false
                                    : props.formik.initialValues.irrelevant);
                                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.antalPlanlagteForloebToCreate), kladde
                                    ? 1
                                    : props.formik.initialValues.antalPlanlagteForloebToCreate)
                            }}
                        />
                    }
                </div>
            </div>
            {props.forloebGodkendtDato && forloebGodkendt &&
                <div className="row">
                    <div className="col-sm-12">
                        <div className="alert alert-success" role="alert">
                            {Localizer.forloebpage_KanIkkeRedigeresDaDetErGodkendt(props.forloebGodkendtDato)}
                        </div>
                    </div>
                </div>
            }
            <div className="row">
                <div className="col-sm-12">
                    <ValidationDatepicker
                        model={{
                            label: Localizer.startdato(),
                            placeholder: Localizer.startdatoPlaceholder(),
                            htmlName: nameof<CrudForloebInputModel>(x => x.startDato),
                        }}
                        inputId={nameof<CrudForloebInputModel>(x => x.startDato) + props.modalId}
                        formikProps={props.formik}
                        dateSelected={(date) => setSelectedStartDate((previous) => {
                            prevStartDate.current = previous;

                            const dateHasChanged = date.dateWithoutTimeFormat(true) !== previous?.dateWithoutTimeFormat(true);

                            if (dateHasChanged) {
                                const sixMonthsMinusADayFromDate = date.addMonths(6).subtractDays(1);
                                slutDateTimePickerModuleRef.current?.setValue(sixMonthsMinusADayFromDate);
                            }

                            return date;
                        })}
                        readOnly={props.forloebIsAfsluttet || props.forloebIsGodkendt || props.forloebIsEvalueret}
                    />
                </div>
            </div>

            <div className="row">
                <div className="col-sm-12">
                    <ValidationDatepicker
                        ref={slutDateTimePickerModuleRef}
                        model={{
                            label: Localizer.slutdato(),
                            placeholder: Localizer.slutdatoPlaceholder(),
                            htmlName: nameof<CrudForloebInputModel>(x => x.slutDato)
                        }}
                        inputId={nameof<CrudForloebInputModel>(x => x.slutDato) + props.modalId}
                        formikProps={props.formik}
                        dateSelected={(date) => setSelectedEndDate((prevState) => {
                            prevEndDate.current = prevState;
                            return date;
                        })}
                        readOnly={props.forloebIsAfsluttet || props.forloebIsGodkendt || props.forloebIsEvalueret}
                        additionalIconClasses={"sharp-border"}
                        appendToInputGroup={
                            <DatePickerActionButton
                                datePickerRef={slutDateTimePickerModuleRef}
                                disabled={selectedEndDate === undefined || props.forloebIsGodkendt}
                                actionText={Localizer.forloebEdit_AddMonths(6)}
                                onButtonClickedCallback={(module) => !!selectedEndDate && module.setValue(calculateEndDate(selectedEndDate), true)}
                            />
                        }
                    />
                </div>
            </div>

            {forloebDuration && (forloebDuration.months > 0 || forloebDuration.days > 0) &&
                <div className="row">
                    <div className="col-sm-12">
                        <div className="flex-row">
                            <div className=" alert alert-info">
                                {Localizer.forloebVarighedIMaaneder(forloebDuration.months, forloebDuration.days)}
                            </div>
                        </div>
                    </div>
                </div>
            }

            <div className="row">
                <div className="col-sm-12">
                    <>
                        <ValidationDropdown
                            //Reinitialize dropdown when initialValue has fetched:
                            key={initialSelectedStillingOption?.value}
                            model={{
                                label: Localizer.stilling(),
                                placeholder: Localizer.vaelgStilling(),
                                htmlName: nameof<CrudForloebInputModel>(x => x.stillingId),
                            }}
                            fetchAsyncOptions={fetchAsyncLedigeStillingerOptions}
                            initialAsyncValue={initialSelectedStillingOption}
                            fetchAsyncMinSearchLength={3}
                            fetchAsyncDebounceTime={500}
                            fetchAsyncResetCacheOnChange={`${selectedStartDate}-${selectedEndDate}`}
                            readOnly={props.stillingReadonly || props.forloebIsGodkendt || props.forloebIsEvalueret || !selectedStartDate || !selectedEndDate}
                            formikProps={props.formik}
                            itemSelected={(newValue) => onStillingSelected(newValue?.value ?? "")}
                        />

                        {(!!selectedStilling?.erAltidFravaer) &&
                            <div className=" alert alert-warning">
                                {Localizer.forloebpage_fravaersStillingSelectedInfo()}
                            </div>
                        }
                        {(!!selectedStilling?.overlappendeForloeb && !selectedStilling.erAltidFravaer) &&
                            <div className=" alert alert-warning">
                                {Localizer.forloebpage_derErForloebSomOverlapperDato()}
                            </div>
                        }

                    </>
                </div>
            </div>
            {(!props.formik.initialValues.erFravaer && !selectedStilling?.erAltidFravaer && !props.formik.values.forloebKladde) &&
                <div className="flex-row">
                    <div>
                        <ValidationSwitch
                            model={{
                                label: Localizer.skalIndgaaIEvalueringsstatistik(),
                                placeholder: Localizer.skalIndgaaIEvalueringsstatistik(),
                                htmlName: nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik)
                            }}
                            labelInfoTooltipText={forloebErEvalueret ? Localizer.forloebEdit_forloebErEvalueret() : undefined}
                            inputId={nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik) + props.modalId}
                            formikProps={props.formik}
                            readOnly={(props.forloebIsAfsluttet || props.forloebIsEvalueret) || props.formik.values.irrelevant || props.formik.values.forloebKladde}
                            onChangeCallback={(e) => {

                                const skalIndgaaIEvalueringsstatistik = e.target.checked;
                                if (selectedEndDate && skalIndgaaIEvalueringsstatistik) {
                                    const senesteEvalueringsdato = selectedEndDate.addMonths(monthsLatestEvalDateIsFromEndDate);
                                    formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.senesteDatoForEvaluering), senesteEvalueringsdato);
                                    setSelectedSenestEvalueringsdato(senesteEvalueringsdato);

                                    if (senesteEvalueringsdato)
                                        senestEvalueringsdatoDateTimePickerModuleRef.current?.setValue(senesteEvalueringsdato, true);
                                }
                            }}
                        />
                        <ValidationSwitch
                            model={{
                                label: Localizer.forloebpage_notRelevant(),
                                placeholder: Localizer.forloebpage_notRelevant(),
                                htmlName: nameof<CrudForloebInputModel>(x => x.irrelevant)
                            }}
                            inputId={nameof<CrudForloebInputModel>(x => x.irrelevant) + props.modalId}
                            readOnly={props.forloebIsAfsluttet || props.forloebIsEvalueret || !currentUser.IsAdminOrVus() || props.formik.values.forloebKladde}
                            labelInfoTooltipText={!currentUser.IsAdminOrVus() && !props.formik.initialValues.irrelevant ? Localizer.forloebEdit_canSetForloebIrrelevant() : undefined}
                            formikProps={props.formik}
                            onChangeCallback={(e) => {
                                const irrelevant = e.target.checked;
                                formikSetFieldValue(nameof<CrudForloebInputModel>(x => x.skalIndgaaIEvalueringsstatistik), irrelevant
                                    ? false
                                    : props.formik.initialValues.skalIndgaaIEvalueringsstatistik);
                            }}
                        />
                        {props.formik.values.irrelevant &&
                            <div className="alert alert-info" role={"alert"}>
                                {Localizer.forloebpage_notRelevantCreateDescription()}
                            </div>
                        }

                    </div>
                    {props.formik.values.skalIndgaaIEvalueringsstatistik &&
                        <div className={`${forloebErEvalueret ? "flex-space" : ""}`}>
                            <ValidationDatepicker
                                ref={senestEvalueringsdatoDateTimePickerModuleRef}
                                model={{
                                    label: Localizer.forloebEdit_SenesteDatoForEvaluering(),
                                    placeholder: Localizer.datoPlaceholder(),
                                    htmlName: nameof<CrudForloebInputModel>(x => x.senesteDatoForEvaluering)
                                }}
                                inputId={nameof<CrudForloebInputModel>(x => x.senesteDatoForEvaluering) + props.modalId}
                                formikProps={props.formik}
                                readOnly={props.forloebIsAfsluttet || props.forloebIsEvalueret || !props.formik.values.skalIndgaaIEvalueringsstatistik}
                                dateSelected={(date) => setSelectedSenestEvalueringsdato(date)}
                                appendToInputGroup={
                                    <DatePickerActionButton
                                        disabled={props.forloebIsAfsluttet || props.forloebIsEvalueret || !props.formik.values.skalIndgaaIEvalueringsstatistik}
                                        datePickerRef={senestEvalueringsdatoDateTimePickerModuleRef}
                                        actionText={forloebErEvalueret ? Localizer.global_evalueret() : Localizer.forloebEdit_AddMonths(6)}
                                        btnIcon={forloebErEvalueret ? EmblaIcons.Success : undefined}
                                        onButtonClickedCallback={(module) => selectedSenestEvalueringsdato && module.setValue(calculateEndDate(selectedSenestEvalueringsdato), true)}
                                    />
                                }
                            />
                        </div>

                    }
                </div>
            }
            {props.formik.values.forloebKladde && !isCreateFromKladde &&
                <div className={classNames("flex-row")}>
                    <ValidationInput
                        type={"number"}
                        min={"1"}
                        max={"10"}
                        labelInfoTooltipText={Localizer.forloebpage_oprettelseAfFlerePlanlagtForloebTooltip()}
                        model={{
                            label: Localizer.forloebpage_antalPlanlagteForloeb(),
                            placeholder: Localizer.forloebpage_derSkalOprettesMellemEtOgTiPlanlagteForloeb(),
                            htmlName: nameof<CrudForloebInputModel>(x => x.antalPlanlagteForloebToCreate)
                        }}
                        inputId={nameof<CrudForloebInputModel>(x => x.antalPlanlagteForloebToCreate) + props.modalId}
                        formikProps={props.formik}
                    />
                </div>
            }
            {(!!selectedStilling?.erAltidFravaer) &&
                <div className="row">
                    <div className="col-sm-12">
                        <ValidationDropdown
                            model={{
                                label: Localizer.Fravaerskoder(),
                                placeholder: Localizer.SelectFravaerskoder(),
                                htmlName: nameof<CrudForloebInputModel>(x => x.fravaerskode)
                            }}
                            options={fravaerskoderTypeSelectOptions}
                            formikProps={props.formik}
                        />
                    </div>
                </div>
            }
        </>
    )
}
